import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface SkippedBook {
  book_id: string;
  tier: number;
  title: string;
  authors: string[];
  cover_url?: string;
  skipped_at: string;
}

export function useRankLater() {
  const [skippedBooks, setSkippedBooks] = useState<SkippedBook[]>([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  // Load skipped books from user_tier_scores where last comparison was skipped
  const loadSkippedBooks = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      // Get user tier scores that haven't been properly compared yet
      // We'll use a simple approach: books with scores but no recent pairwise events
      const { data: tierScores } = await supabase
        .from('user_tier_scores')
        .select(`
          book_id,
          tier,
          score,
          last_update,
          placement_complete
        `)
        .eq('user_id', user.id)
        .eq('placement_complete', false)
        .order('last_update', { ascending: true });

      if (!tierScores || tierScores.length === 0) {
        setSkippedBooks([]);
        return;
      }

      // Get book details separately
      const bookIds = tierScores.map(ts => ts.book_id);
      const { data: books } = await supabase
        .from('books')
        .select('id, title, authors, cover_url')
        .in('id', bookIds);

      if (tierScores) {
        // Check which books have minimal pairwise events (indicating they were mostly skipped)        
        const { data: pairwiseEvents } = await supabase
          .from('user_pairwise_events')
          .select('winner_book_id, loser_book_id')
          .eq('user_id', user.id)
          .or(`winner_book_id.in.(${bookIds.join(',')}),loser_book_id.in.(${bookIds.join(',')})`);

        // Books with fewer than 2 pairwise comparisons are candidates for "Rank Later"
        const bookComparisonCounts = new Map<string, number>();
        pairwiseEvents?.forEach(event => {
          if (event.winner_book_id) {
            bookComparisonCounts.set(event.winner_book_id, (bookComparisonCounts.get(event.winner_book_id) || 0) + 1);
          }
          if (event.loser_book_id) {
            bookComparisonCounts.set(event.loser_book_id, (bookComparisonCounts.get(event.loser_book_id) || 0) + 1);
          }
        });

        const skipped = tierScores
          .filter(ts => (bookComparisonCounts.get(ts.book_id) || 0) < 2)
          .map(ts => {
            const book = books?.find(b => b.id === ts.book_id);
            return {
              book_id: ts.book_id,
              tier: ts.tier,
              title: book?.title || 'Unknown Title',
              authors: book?.authors || [],
              cover_url: book?.cover_url,
              skipped_at: ts.last_update || new Date().toISOString()
            };
          })
          .filter(book => book.title !== 'Unknown Title');

        // Deduplicate by book_id - keep only the most recent tier for each book
        const bookMap = new Map<string, typeof skipped[0]>();
        skipped.forEach(book => {
          const existing = bookMap.get(book.book_id);
          if (!existing || new Date(book.skipped_at) > new Date(existing.skipped_at)) {
            bookMap.set(book.book_id, book);
          }
        });

        const deduped = Array.from(bookMap.values())
          .sort((a, b) => new Date(a.skipped_at).getTime() - new Date(b.skipped_at).getTime())
          .slice(0, 20); // Limit to most recent 20

        setSkippedBooks(deduped);
      }
    } catch (error) {
      console.error('Error loading skipped books:', error);
      toast.error('Failed to load books to rank');
    } finally {
      setLoading(false);
    }
  };

  // Mark a book as no longer skipped (after completing a pairwise comparison)
  const markBookAsRanked = (bookId: string) => {
    setSkippedBooks(prev => prev.filter(book => book.book_id !== bookId));
  };

  // Add a book to the skipped list (when user skips during pairwise comparison)
  const addSkippedBook = async (bookId: string, tier: number) => {
    if (!user) return;

    // The book should already be in user_tier_scores, so we don't need to add it
    // Just refresh the list to pick up the change
    await loadSkippedBooks();
  };

  useEffect(() => {
    if (user) {
      loadSkippedBooks();
    }
  }, [user]);

  return {
    skippedBooks,
    loading,
    loadSkippedBooks,
    markBookAsRanked,
    addSkippedBook,
  };
}